import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, ArrowRight } from 'lucide-react';
import { CourseTOC } from '../../components/CourseTOC';
import { CourseCompletion } from '../../components/CourseCompletion';
import { VideoPlayer } from '../../components/VideoPlayer';
import { CourseProgress } from '../../components/CourseProgress';
import { QuizFlow } from '../../components/QuizFlow';
import { useCourseView } from '../../hooks/useCourseView';

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1024); // 1024px is the lg breakpoint
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return isMobile;
};

export function CourseViewPage() {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    
    const {
        // State
        course,
        isLoading,
        currentLessonIndex,
        maxPlayed,
        initialPlayedSeconds,
        setVideoDuration,
        isCompleted,
        isUpdating,
        showQuiz,
        quizResult,
        showQuizResults,
        checkingQuizStatus,
        
        // Derived state
        currentLesson,
        canMarkComplete,
        
        // Handlers
        handleProgress,
        handleSelectLesson,
        handlePreviousLesson,
        handleNextLesson,
        handleMarkComplete,
        handleQuizComplete,
        handleQuizRetake,
        handleQuizContinue,
    } = useCourseView();

    // Show loading state
    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
            </div>
        );
    }

    // Show loading state while checking quiz status
    if (checkingQuizStatus) {
        return (
            <div className="bg-white p-8 rounded-lg shadow border border-slate-200">
                <div className="flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600 mr-3" />
                    <span className="text-lg text-muted">Checking quiz status...</span>
                </div>
            </div>
        );
    }

    // Show error state if no course found
    if (!course) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-500">Course not found</p>
                <button 
                    onClick={() => navigate('/courses')}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    Back to Courses
                </button>
            </div>
        );
    }

    // Show course completion
    if (isCompleted && !showQuiz && !showQuizResults) {
        return (
            <CourseCompletion 
                courseName={course.title}
                onBack={() => navigate('/courses')}
            />
        );
    }

    // Show quiz flow
    if (showQuiz || showQuizResults) {
        return (
            <div className="flex flex-col lg:flex-row gap-6">
                {isMobile ? (
                    <>
                        <CourseTOC 
                            course={course} 
                            currentLessonIndex={currentLessonIndex} 
                            onSelectLesson={handleSelectLesson}
                        />
                        <div className="flex-grow">
                            <QuizFlow
                                showQuiz={showQuiz}
                                showQuizResults={showQuizResults}
                                quizResult={quizResult}
                                courseId={course.id}
                                onQuizComplete={handleQuizComplete}
                                onQuizRetake={handleQuizRetake}
                                onQuizContinue={handleQuizContinue}
                            />
                        </div>
                    </>
                ) : (
                    <>
                        <div className="flex-grow">
                            <QuizFlow
                                showQuiz={showQuiz}
                                showQuizResults={showQuizResults}
                                quizResult={quizResult}
                                courseId={course.id}
                                onQuizComplete={handleQuizComplete}
                                onQuizRetake={handleQuizRetake}
                                onQuizContinue={handleQuizContinue}
                            />
                        </div>
                        <div className="w-[500px]">
                        <CourseTOC 
                            course={course} 
                            currentLessonIndex={currentLessonIndex} 
                            onSelectLesson={handleSelectLesson}
                        />
                        </div>
                    </>
                )}
            </div>
        );
    }

    // Show error state if course has no lessons
    if (!course.lessons || course.lessons.length === 0) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-500">This course has no lessons available</p>
                <button 
                    onClick={() => navigate('/courses')}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    Back to Courses
                </button>
            </div>
        );
    }

    // Main course view
    return (
        <div className="flex flex-col lg:flex-row gap-6">
            {isMobile ? (
                <>
                    <div className="flex-grow">
                        <div className="bg-white rounded-lg shadow border border-slate-200">
                            <div className="p-6">
                                <h2 className="text-xl font-semibold text-slate-800 mb-4">
                                    {currentLesson?.title}
                                </h2>
                                
                                <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden mb-6 shadow-sm">
                                    <VideoPlayer
                                        videoUrl={currentLesson?.videoUrl || ''}
                                        onProgress={handleProgress}
                                        maxPlayed={maxPlayed}
                                        initialPlayedSeconds={initialPlayedSeconds}
                                        onDuration={setVideoDuration}
                                    />
                                </div>
                                
                                {/* Navigation Controls - Moved to bottom */}
                                <div className="flex items-center justify-between mb-6">
                                    <button
                                        onClick={handlePreviousLesson}
                                        disabled={currentLessonIndex === 0}
                                        className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                            currentLessonIndex > 0
                                                ? 'text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300'
                                                : 'text-slate-400 bg-slate-50 cursor-not-allowed border border-slate-200'
                                        }`}
                                    >
                                        <ArrowLeft size={16} className="mr-2" />
                                        Previous
                                    </button>

                                    <span className="text-sm font-medium text-slate-700">
                                        Lesson {currentLessonIndex + 1} of {course.lessons.length}
                                    </span>

                                    <button
                                        onClick={handleNextLesson}
                                        disabled={currentLessonIndex >= course.lessons.length - 1}
                                        className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                            currentLessonIndex < course.lessons.length - 1
                                                ? 'text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300'
                                                : 'text-slate-400 bg-slate-50 cursor-not-allowed border border-slate-200'
                                        }`}
                                    >
                                        Next
                                        <ArrowRight size={16} className="ml-2" />
                                    </button>
                                </div>
                            </div>

                            <CourseProgress
                                canMarkComplete={canMarkComplete}
                                isUpdating={isUpdating}
                                onMarkComplete={handleMarkComplete}
                            />
                        </div>
                    </div>
                    <CourseTOC 
                        course={course} 
                        currentLessonIndex={currentLessonIndex} 
                        onSelectLesson={handleSelectLesson}
                    />
                </>
            ) : (
                <>
                    <div className="flex-1 min-w-0">
                        <div className="bg-white rounded-lg shadow border border-slate-200">
                            <div className="p-6">
                                <h2 className="text-xl font-semibold text-slate-800 mb-4">
                                    {currentLesson?.title}
                                </h2>
                                
                                <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden mb-6 shadow-sm">
                                    <VideoPlayer
                                        videoUrl={currentLesson?.videoUrl || ''}
                                        onProgress={handleProgress}
                                        maxPlayed={maxPlayed}
                                        initialPlayedSeconds={initialPlayedSeconds}
                                        onDuration={setVideoDuration}
                                    />
                                </div>
                                
                                {/* Navigation Controls - Moved to bottom */}
                                <div className="flex items-center justify-between mb-6">
                                    <button
                                        onClick={handlePreviousLesson}
                                        disabled={currentLessonIndex === 0}
                                        className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                            currentLessonIndex > 0
                                                ? 'text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300'
                                                : 'text-slate-400 bg-slate-50 cursor-not-allowed border border-slate-200'
                                        }`}
                                    >
                                        <ArrowLeft size={16} className="mr-2" />
                                        Previous
                                    </button>

                                    <span className="text-sm font-medium text-slate-700">
                                        Lesson {currentLessonIndex + 1} of {course.lessons.length}
                                    </span>

                                    <button
                                        onClick={handleNextLesson}
                                        disabled={currentLessonIndex >= course.lessons.length - 1}
                                        className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                            currentLessonIndex < course.lessons.length - 1
                                                ? 'text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300'
                                                : 'text-slate-400 bg-slate-50 cursor-not-allowed border border-slate-200'
                                        }`}
                                    >
                                        Next
                                        <ArrowRight size={16} className="ml-2" />
                                    </button>
                                </div>
                            </div>
                            
                            <CourseProgress
                                canMarkComplete={canMarkComplete}
                                isUpdating={isUpdating}
                                onMarkComplete={handleMarkComplete}
                            />
                        </div>
                    </div>
                    <div className="flex-shrink-0 w-[500px]">
                        <CourseTOC 
                            course={course} 
                            currentLessonIndex={currentLessonIndex} 
                            onSelectLesson={handleSelectLesson}
                        />
                    </div>
                </>
            )}
        </div>
    );
}